import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import ColumnFilter from '../../common/ColumnFilter';
import stockReorderApi, { errorMessage } from '../../../api/stockReorderApi';
import StockUploadModal from './StockUploadModal';

/**
 * Stock Availability — the branch's uploaded ERP Stock Report.
 *
 * The supplier on each row comes from the server, resolved from Supplier
 * Mapping at read time; it is never stored with the stock. So this tab only
 * reloads — it never has to be re-uploaded — to pick up a mapping change.
 *
 * The whole report (~5k rows) arrives in one response, so filtering, grouping,
 * sorting and export all run here on the same rows: what is exported is
 * exactly what is on screen.
 */

export const UNMAPPED = 'Unmapped supplier';
const GROUP_KEY = 'stockAvailability.groupBy';
const ROW_PAGE = 300;
// "Expand all" beyond this many rows would mount tens of thousands of cells.
const EXPAND_ALL_LIMIT = 1500;

/**
 * `filter` marks the columns with an Excel-style value filter. Only columns whose
 * values repeat get one: Part No and Description are unique per row (a 5k-long
 * checkbox list duplicating the search box), and Cost/Qty/Value have thousands of
 * distinct numbers. Those are served by the search box and sorting instead.
 */
const COLUMNS = [
    { key: 'supplier', label: 'Supplier', width: '17%', wrap: true, filter: true },
    { key: 'manufacturer', label: 'Manufacturer', width: '15%', wrap: true, filter: true },
    { key: 'part_number', label: 'Part No', width: '8%' },
    { key: 'description', label: 'Description', width: '23%', wrap: true },
    { key: 'unit', label: 'Unit', width: '7%', filter: true },
    { key: 'cost', label: 'Cost', width: '9%', num: true, digits: 3 },
    { key: 'qty', label: 'Qty', width: '10%', num: true, digits: 3 },
    { key: 'value', label: 'Value', width: '11%', num: true, digits: 2 }
];
const FILTER_COLUMNS = COLUMNS.filter((c) => c.filter);

// Only the numbers are sortable; text columns are what the groups and filters are for.
const SORT_OPTIONS = [
    ['value:desc', 'Value — high to low'],
    ['value:asc', 'Value — low to high'],
    ['qty:desc', 'Qty — high to low'],
    ['qty:asc', 'Qty — low to high'],
    ['cost:desc', 'Cost — high to low'],
    ['cost:asc', 'Cost — low to high']
];

const NARROW_QUERY = '(max-width: 767px)';

/** Phones get a card list: an 8-column table cannot fit, and scrolling it sideways hides the group names. */
const useIsNarrow = () => {
    const [narrow, setNarrow] = useState(() => Boolean(window.matchMedia?.(NARROW_QUERY).matches));
    useEffect(() => {
        const mq = window.matchMedia?.(NARROW_QUERY);
        if (!mq) return undefined;
        const onChange = (e) => setNarrow(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return narrow;
};

const EMPTY_FILTERS = Object.fromEntries(FILTER_COLUMNS.map((c) => [c.key, []]));

// Cached formatters: `toLocaleString` builds a new formatter on every call,
// which across ~5k rows x 8 columns was most of a keystroke's cost.
const NUMBER_FORMATS = {};
const fmt = (n, digits = 2) => {
    if (!NUMBER_FORMATS[digits]) {
        NUMBER_FORMATS[digits] = new Intl.NumberFormat(undefined, { maximumFractionDigits: digits });
    }
    return NUMBER_FORMATS[digits].format(Number(n || 0));
};
const MONEY = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n) => MONEY.format(Number(n || 0));

const formatCell = (item, col) => {
    if (col.key === 'supplier') return item.supplier || UNMAPPED;
    if (col.num) return fmt(item[col.key], col.digits);
    return String(item[col.key] ?? '');
};

/**
 * The value a row shows — and filters on — for a column. Read from `_t`, which
 * is computed once per load, so filtering and search only compare strings.
 */
const cellText = (item, col) => item._t[col.key];

const daysOld = (isoDate) => {
    if (!isoDate) return null;
    const [y, m, d] = isoDate.split('-').map(Number);
    const today = new Date();
    const start = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((start - Date.UTC(y, m - 1, d)) / 86400000);
};

const readGroupBy = () => {
    try {
        return localStorage.getItem(GROUP_KEY) || 'supplier';
    } catch (e) {
        return 'supplier';
    }
};

const StockAvailabilityTab = ({
    businessUnits,
    branchId,
    onBranchChange,
    canUpload,
    historyUpload,
    onBackToCurrent,
    onToast
}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [groupBy, setGroupBy] = useState(readGroupBy);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [sort, setSort] = useState({ key: 'value', dir: 'desc' });
    const [expanded, setExpanded] = useState({});
    const [shown, setShown] = useState({});
    const [showUpload, setShowUpload] = useState(false);

    const historyId = historyUpload?.id || '';

    const load = useCallback(async () => {
        if (!branchId) return;
        setLoading(true);
        setError('');
        try {
            setData(await stockReorderApi.getStock(branchId, historyId));
        } catch (err) {
            setData(null);
            setError(errorMessage(err, 'Could not load stock'));
        } finally {
            setLoading(false);
        }
    }, [branchId, historyId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        try {
            localStorage.setItem(GROUP_KEY, groupBy);
        } catch (e) {
            // Remembering the choice is a convenience only.
        }
        setExpanded({});
        setShown({});
    }, [groupBy]);

    const items = useMemo(
        () =>
            (data?.items || []).map((item) => ({
                ...item,
                _t: Object.fromEntries(COLUMNS.map((c) => [c.key, formatCell(item, c)])),
                _search: `${item.part_number} ${item.description}`.toLowerCase()
            })),
        [data]
    );
    const upload = data?.upload || null;

    // Typing stays responsive; the table catches up a moment later.
    const deferredSearch = useDeferredValue(search);

    /** Rows passing search + every column filter except `skipKey`. */
    const applyFilters = useCallback(
        (skipKey) => {
            const q = deferredSearch.trim().toLowerCase();
            const active = FILTER_COLUMNS.filter((c) => c.key !== skipKey && filters[c.key].length);
            const sets = active.map((c) => [c, new Set(filters[c.key])]);
            return items.filter((item) => {
                if (q && !item._search.includes(q)) return false;
                return sets.every(([c, set]) => set.has(cellText(item, c)));
            });
        },
        [items, deferredSearch, filters]
    );

    const filtered = useMemo(() => applyFilters(null), [applyFilters]);

    // Each column's choices narrow by the other columns' filters, like Excel.
    const filterOptions = useMemo(() => {
        const out = {};
        FILTER_COLUMNS.forEach((col) => {
            const counts = new Map();
            applyFilters(col.key).forEach((item) => {
                const v = cellText(item, col);
                counts.set(v, (counts.get(v) || 0) + 1);
            });
            const values = [...counts.entries()].map(([value, count]) => ({ value, count }));
            values.sort((a, b) => a.value.localeCompare(b.value));
            out[col.key] = values;
        });
        return out;
    }, [applyFilters]);

    const sorted = useMemo(() => {
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => (Number(a[sort.key]) - Number(b[sort.key])) * dir);
    }, [filtered, sort]);

    const groups = useMemo(() => {
        if (groupBy === 'none') return null;
        const map = new Map();
        sorted.forEach((item) => {
            const key =
                groupBy === 'supplier' ? item.supplier || UNMAPPED : item.manufacturer || '(No manufacturer)';
            if (!map.has(key)) map.set(key, { key, items: [], qty: 0, value: 0, cost: 0 });
            const g = map.get(key);
            g.items.push(item);
            g.qty += Number(item.qty) || 0;
            g.value += Number(item.value) || 0;
            g.cost = Math.max(g.cost, Number(item.cost) || 0);
        });
        // Groups follow the chosen sort too, not just the rows inside them —
        // groups start collapsed, so ordering only the rows looked like the sort
        // did nothing. Qty/Value order by the group total; Cost (which does not
        // add up) by the group's highest-cost item.
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...map.values()].sort((a, b) => {
            // Unmapped is a normal group, just always listed last.
            if (a.key === UNMAPPED) return 1;
            if (b.key === UNMAPPED) return -1;
            return (a[sort.key] - b[sort.key]) * dir;
        });
    }, [sorted, groupBy, sort]);

    const totals = useMemo(
        () =>
            filtered.reduce(
                (acc, i) => ({ qty: acc.qty + (Number(i.qty) || 0), value: acc.value + (Number(i.value) || 0) }),
                { qty: 0, value: 0 }
            ),
        [filtered]
    );

    const mappedCount = useMemo(() => items.filter((i) => i.supplier).length, [items]);
    const mappedValue = useMemo(
        () => items.reduce((s, i) => s + (i.supplier ? Number(i.value) || 0 : 0), 0),
        [items]
    );

    const activeFilterCount =
        FILTER_COLUMNS.filter((c) => filters[c.key].length).length + (search.trim() ? 1 : 0);
    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setSearch('');
    };

    const toggleSort = (key) =>
        setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

    const isNarrow = useIsNarrow();

    const renderFilter = (c) => (
        <ColumnFilter
            column={c.key}
            label={c.label}
            selected={filters[c.key]}
            options={filterOptions[c.key]}
            onChange={(values) => setFilters((f) => ({ ...f, [c.key]: values }))}
        />
    );

    const toggleGroup = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

    const renderCards = (rows, key) => {
        const limit = shown[key] || ROW_PAGE;
        return (
            <>
                {rows.slice(0, limit).map((item) => (
                    <div className="sr-stock-item" key={item.part_key}>
                        <div className="sr-stock-item-main">
                            <div className="sr-stock-item-desc">{item.description}</div>
                            <div className="sr-muted">
                                {item.part_number} · {item.unit}
                                {groupBy !== 'manufacturer' && item.manufacturer ? ` · ${item.manufacturer}` : ''}
                            </div>
                            {groupBy !== 'supplier' && (
                                <div className={item.supplier ? 'sr-muted' : 'sr-unmapped-text'}>
                                    {item.supplier || UNMAPPED}
                                </div>
                            )}
                        </div>
                        <div className="sr-stock-item-nums">
                            <strong>{item._t.qty}</strong>
                            <span className="sr-muted">{item._t.value}</span>
                        </div>
                    </div>
                ))}
                {rows.length > limit && (
                    <div className="sr-empty">
                        Showing {limit.toLocaleString()} of {rows.length.toLocaleString()}{' '}
                        <button className="sr-link" onClick={() => setShown((s) => ({ ...s, [key]: limit + ROW_PAGE }))}>
                            Show more
                        </button>
                    </div>
                )}
            </>
        );
    };

    const setAllExpanded = (open) =>
        setExpanded(open && groups ? Object.fromEntries(groups.map((g) => [g.key, true])) : {});

    const branch = businessUnits.find((bu) => bu.id === branchId);
    const branchName = branch?.branch || branch?.name || 'Branch';

    const exportFile = (kind) => {
        const header = COLUMNS.map((c) => c.label);
        const row = (i) =>
            COLUMNS.map((c) => (c.num ? Number(i[c.key]) || 0 : c.key === 'supplier' ? i.supplier || UNMAPPED : i[c.key]));
        const aoa = [];
        if (kind === 'xlsx') {
            aoa.push([`${branchName} — Stock as on ${upload?.as_on_date || ''}`]);
            aoa.push([
                `Grouped by: ${groupBy === 'none' ? 'None' : groupBy === 'supplier' ? 'Supplier' : 'Manufacturer'}` +
                    (activeFilterCount ? ` · ${activeFilterCount} filter(s) applied` : '')
            ]);
            aoa.push([]);
        }
        aoa.push(header);
        if (groups && kind === 'xlsx') {
            groups.forEach((g) => {
                aoa.push([`${g.key} (${g.items.length} items)`]);
                g.items.forEach((i) => aoa.push(row(i)));
                aoa.push(['', '', '', `Subtotal — ${g.key}`, '', '', g.qty, g.value]);
                aoa.push([]);
            });
        } else {
            sorted.forEach((i) => aoa.push(row(i)));
        }
        if (kind === 'xlsx') aoa.push(['', '', '', 'Grand total', '', '', totals.qty, totals.value]);

        const safeBranch = branchName.replace(/[^A-Za-z0-9]+/g, '_');
        const base = `Stock_${safeBranch}_${upload?.as_on_date || 'current'}${groups ? `_by-${groupBy}` : ''}`;
        const sheet = XLSX.utils.aoa_to_sheet(aoa);
        if (kind === 'csv') {
            const blob = new Blob(['﻿' + XLSX.utils.sheet_to_csv(sheet)], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${base}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }
        sheet['!cols'] = [28, 20, 10, 48, 8, 10, 10, 12].map((wch) => ({ wch }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, sheet, 'Stock');
        XLSX.writeFile(wb, `${base}.xlsx`);
    };

    const renderRows = (rows, key) => {
        const limit = shown[key] || ROW_PAGE;
        return (
            <>
                {rows.slice(0, limit).map((item) => (
                    <tr key={item.part_key}>
                        {COLUMNS.map((c) => (
                            <td
                                key={c.key}
                                className={`${c.num ? 'sr-right' : ''} ${c.wrap ? 'sr-wrap' : ''} ${
                                    c.key === 'supplier' && !item.supplier ? 'sr-unmapped' : ''
                                }`}
                            >
                                {cellText(item, c)}
                            </td>
                        ))}
                    </tr>
                ))}
                {rows.length > limit && (
                    <tr>
                        <td colSpan={COLUMNS.length} className="sr-empty">
                            Showing {limit.toLocaleString()} of {rows.length.toLocaleString()}{' '}
                            <button
                                className="sr-link"
                                onClick={() => setShown((s) => ({ ...s, [key]: limit + ROW_PAGE * 3 }))}
                            >
                                Show more
                            </button>
                        </td>
                    </tr>
                )}
            </>
        );
    };

    const age = daysOld(upload?.as_on_date);
    const ageClass = age == null ? '' : age > 7 ? 'sr-age-old' : age > 2 ? 'sr-age-warn' : 'sr-age-ok';

    return (
        <div className="sr-stack">
            <section className="sr-card">
                <div className="sr-stock-toolbar">
                    <div className="sr-field sr-stock-branch">
                        <label htmlFor="sr-stock-branch">Branch</label>
                        <select
                            id="sr-stock-branch"
                            value={branchId}
                            onChange={(e) => onBranchChange(e.target.value)}
                            disabled={Boolean(historyUpload)}
                        >
                            {businessUnits.map((bu) => (
                                <option key={bu.id} value={bu.id}>
                                    {bu.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="sr-actions">
                        {canUpload && !historyUpload && (
                            <button className="sr-btn sr-btn-primary" onClick={() => setShowUpload(true)} disabled={!branchId}>
                                ⬆ Upload stock file
                            </button>
                        )}
                        <button className="sr-btn" onClick={() => exportFile('xlsx')} disabled={!filtered.length}>
                            ⬇ Excel
                        </button>
                        <button className="sr-btn" onClick={() => exportFile('csv')} disabled={!filtered.length}>
                            ⬇ CSV
                        </button>
                        <button className="sr-btn" onClick={load} disabled={loading || !branchId}>
                            ↻ Refresh
                        </button>
                    </div>
                </div>

                {historyUpload && (
                    <div className="sr-snapshot-banner">
                        <span>
                            Viewing history — stock as on {historyUpload.as_on_date} (read-only). Suppliers
                            reflect today's Supplier Mapping.
                        </span>
                        <button className="sr-btn sr-btn-sm" onClick={onBackToCurrent}>
                            Back to current
                        </button>
                    </div>
                )}

                {error && <div className="sr-alert sr-alert-error">{error}</div>}
                {loading && <div className="sr-empty">Loading stock…</div>}

                {!loading && !error && branchId && !upload && (
                    <div className="sr-empty">
                        <strong>No stock report uploaded for {branchName} yet.</strong>
                        <p>
                            {canUpload
                                ? 'Upload the ERP "Stock Report" to see current stock availability.'
                                : 'Ask a stock administrator to upload the ERP stock report.'}
                        </p>
                        {canUpload && (
                            <button className="sr-btn sr-btn-primary" onClick={() => setShowUpload(true)}>
                                ⬆ Upload stock file
                            </button>
                        )}
                    </div>
                )}

                {!loading && upload && (
                    <div className="sr-stats">
                        <div className="sr-stat">
                            <span className="sr-stat-label">Stock as on</span>
                            <span className="sr-stat-value">{upload.as_on_date}</span>
                            {age != null && (
                                <span className={`sr-age ${ageClass}`}>
                                    ● {age <= 0 ? 'today' : `${age} day${age === 1 ? '' : 's'} old`}
                                </span>
                            )}
                        </div>
                        <div className="sr-stat">
                            <span className="sr-stat-label">Items</span>
                            <span className="sr-stat-value">{items.length.toLocaleString()}</span>
                        </div>
                        <div className="sr-stat">
                            <span className="sr-stat-label">Total qty</span>
                            <span className="sr-stat-value">{fmt(upload.total_qty)}</span>
                        </div>
                        <div className="sr-stat">
                            <span className="sr-stat-label">Stock value</span>
                            <span className="sr-stat-value">{money(upload.total_value)}</span>
                        </div>
                        <div className="sr-stat">
                            <span className="sr-stat-label">Supplier mapped</span>
                            <span className="sr-stat-value">{mappedCount.toLocaleString()}</span>
                            <span className="sr-muted">
                                {(items.length - mappedCount).toLocaleString()} unmapped ·{' '}
                                {upload.total_value ? Math.round((mappedValue / upload.total_value) * 100) : 0}% of value
                                mapped
                            </span>
                        </div>
                    </div>
                )}
            </section>

            {!loading && upload && (
                <section className="sr-card">
                    <div className="sr-stock-controls">
                        <div className="sr-mode-toggle">
                            {[
                                ['supplier', 'Supplier'],
                                ['manufacturer', 'Manufacturer'],
                                ['none', 'None']
                            ].map(([id, label]) => (
                                <button
                                    key={id}
                                    className={`sr-chip ${groupBy === id ? 'active' : ''}`}
                                    onClick={() => setGroupBy(id)}
                                >
                                    {id === 'none' ? 'No grouping' : `By ${label}`}
                                </button>
                            ))}
                        </div>
                        <input
                            type="search"
                            className="sr-stock-search"
                            placeholder="Search part no / description"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {groups && (
                            <>
                                <button
                                    className="sr-btn sr-btn-sm"
                                    onClick={() => setAllExpanded(true)}
                                    disabled={filtered.length > EXPAND_ALL_LIMIT}
                                    title={
                                        filtered.length > EXPAND_ALL_LIMIT
                                            ? `Filter to ${EXPAND_ALL_LIMIT.toLocaleString()} items or fewer to expand all groups`
                                            : ''
                                    }
                                >
                                    Expand all
                                </button>
                                <button className="sr-btn sr-btn-sm" onClick={() => setAllExpanded(false)}>
                                    Collapse all
                                </button>
                            </>
                        )}
                        {activeFilterCount > 0 && (
                            <button className="sr-btn sr-btn-sm" onClick={clearFilters}>
                                Clear filters ({activeFilterCount})
                            </button>
                        )}
                    </div>

                    {isNarrow && (
                        <div className="sr-stock-mobilebar">
                            {FILTER_COLUMNS.map((c) => (
                                <span className="sr-mfilter" key={c.key}>
                                    {c.label}
                                    {renderFilter(c)}
                                </span>
                            ))}
                            <select
                                aria-label="Sort by"
                                value={`${sort.key}:${sort.dir}`}
                                onChange={(e) => {
                                    const [key, dir] = e.target.value.split(':');
                                    setSort({ key, dir });
                                }}
                            >
                                {SORT_OPTIONS.map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {isNarrow && (
                        <div className="sr-stock-list">
                            {filtered.length === 0 && (
                                <div className="sr-empty">
                                    No items match these filters.{' '}
                                    <button className="sr-link" onClick={clearFilters}>
                                        Clear filters
                                    </button>
                                </div>
                            )}
                            {groups
                                ? groups.map((g) => (
                                      <div className="sr-stock-group" key={g.key}>
                                          <button
                                              className={`sr-stock-group-head ${g.key === UNMAPPED ? 'sr-group-unmapped' : ''}`}
                                              onClick={() => toggleGroup(g.key)}
                                              aria-expanded={Boolean(expanded[g.key])}
                                          >
                                              <span className="sr-chevron-sm">{expanded[g.key] ? '▼' : '▶'}</span>
                                              <span className="sr-stock-group-name">
                                                  {g.key}
                                                  <span className="sr-muted"> · {g.items.length.toLocaleString()}</span>
                                              </span>
                                              <span className="sr-stock-group-val">{money(g.value)}</span>
                                          </button>
                                          {expanded[g.key] && renderCards(g.items, g.key)}
                                      </div>
                                  ))
                                : renderCards(sorted, '__all')}
                            {filtered.length > 0 && (
                                <div className="sr-stock-mobile-total">
                                    <span>
                                        Total · {filtered.length.toLocaleString()} items
                                    </span>
                                    <strong>{money(totals.value)}</strong>
                                </div>
                            )}
                        </div>
                    )}

                    {!isNarrow && (
                    <div className="sr-table-wrap">
                        <table className="sr-table sr-stock-table">
                            <colgroup>
                                {COLUMNS.map((c) => (
                                    <col key={c.key} style={{ width: c.width }} />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    {COLUMNS.map((c) => (
                                        <th key={c.key} className={c.num ? 'sr-right' : ''}>
                                            <span className="sr-th-inner">
                                                {c.num ? (
                                                    <button
                                                        type="button"
                                                        className={`sr-th-sort ${sort.key === c.key ? 'active' : ''}`}
                                                        onClick={() => toggleSort(c.key)}
                                                        title={`Sort by ${c.label}`}
                                                    >
                                                        {c.label}
                                                        <span className="sr-sort-icon" aria-hidden="true">
                                                            {sort.key === c.key ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <span className="sr-th-label">{c.label}</span>
                                                )}
                                                {c.filter && renderFilter(c)}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={COLUMNS.length} className="sr-empty">
                                            No items match these filters.{' '}
                                            <button className="sr-link" onClick={clearFilters}>
                                                Clear filters
                                            </button>
                                        </td>
                                    </tr>
                                )}
                                {groups
                                    ? groups.map((g) => (
                                          <React.Fragment key={g.key}>
                                              <tr
                                                  className={`sr-group-row ${g.key === UNMAPPED ? 'sr-group-unmapped' : ''}`}
                                                  onClick={() => toggleGroup(g.key)}
                                              >
                                                  <td colSpan={5} className="sr-wrap">
                                                      <span className="sr-chevron-sm">{expanded[g.key] ? '▼' : '▶'}</span>{' '}
                                                      <strong>{g.key}</strong>{' '}
                                                      <span className="sr-muted">
                                                          {g.items.length.toLocaleString()} item
                                                          {g.items.length === 1 ? '' : 's'}
                                                      </span>
                                                      {g.key === UNMAPPED && (
                                                          <span
                                                              className="sr-muted"
                                                              title="No supplier in Supplier Mapping for these part numbers. Importing the ERP supplier export resolves them automatically."
                                                          >
                                                              {' '}
                                                              ⓘ
                                                          </span>
                                                      )}
                                                  </td>
                                                  <td className="sr-right">
                                                      {sort.key === 'cost' && (
                                                          <span className="sr-muted" title="Highest item cost in this group">
                                                              max {fmt(g.cost, 3)}
                                                          </span>
                                                      )}
                                                  </td>
                                                  <td className="sr-right">
                                                      <strong>{fmt(g.qty, 2)}</strong>
                                                  </td>
                                                  <td className="sr-right">
                                                      <strong>{money(g.value)}</strong>
                                                  </td>
                                              </tr>
                                              {expanded[g.key] && renderRows(g.items, g.key)}
                                          </React.Fragment>
                                      ))
                                    : renderRows(sorted, '__all')}
                            </tbody>
                            {filtered.length > 0 && (
                                <tfoot>
                                    <tr className="sr-total-row">
                                        <td colSpan={5}>
                                            Total{activeFilterCount ? ' (filtered)' : ''} ·{' '}
                                            {filtered.length.toLocaleString()} items
                                            {groups ? ` · ${groups.length.toLocaleString()} groups` : ''}
                                        </td>
                                        <td />
                                        <td className="sr-right">{fmt(totals.qty, 2)}</td>
                                        <td className="sr-right">{money(totals.value)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                    )}
                </section>
            )}

            {showUpload && (
                <StockUploadModal
                    businessUnits={businessUnits}
                    defaultBranchId={branchId}
                    onClose={() => setShowUpload(false)}
                    onSaved={(saved) => {
                        setShowUpload(false);
                        onToast?.(
                            saved.status === 'active'
                                ? `Stock updated — as on ${saved.as_on_date}.`
                                : `Saved to history — as on ${saved.as_on_date}.`
                        );
                        if (saved.business_unit_id && saved.business_unit_id !== branchId) {
                            onBranchChange(saved.business_unit_id);
                        } else {
                            load();
                        }
                    }}
                />
            )}
        </div>
    );
};

export default StockAvailabilityTab;
